/**
 * Talking to the admin API.
 *
 * Separate from `../api` on purpose. The learner app's `get`/`post` are module-private
 * and they are shaped for a screen that shows a person what went wrong; the console
 * needs the opposite in one place — a 404 from `/api/admin/me` is *data* here, the
 * thing that decides whether the console exists at all, and it must never reach a
 * user-visible error message (D-20). Rather than widen the learner helpers for a
 * caller with the opposite requirement, this file has its own two.
 *
 * `isError` is shared, because two answers to "is this an ApiError" would drift.
 */

import type { CurriculumNode, Localised, ObjectiveStatus } from '@nabu/shared';
import { isError, type ApiError, type ResearchDocument } from '../api';

async function get<T>(path: string): Promise<T | ApiError> {
  try {
    const response = await fetch(path, { credentials: 'same-origin' });
    const text = await response.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      // An admin endpoint that is not deployed yet is answered by CloudFront's SPA
      // rewrite, which returns index.html with a 200. Parsing first means that
      // arrives as `unreadable_response` rather than as HTML rendered into a table.
      return { error: 'unreadable_response', status: response.status };
    }
    if (!response.ok) {
      return {
        error: (parsed as { error?: string }).error ?? 'request_failed',
        status: response.status,
      };
    }
    return parsed as T;
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

async function post<T>(path: string, body: unknown): Promise<T | ApiError> {
  try {
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await response.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { error: 'unreadable_response', status: response.status };
    }
    if (!response.ok) {
      const failure = parsed as { error?: string; reason?: string };
      return {
        error: failure.error ?? 'request_failed',
        status: response.status,
        ...(failure.reason ? { reason: failure.reason } : {}),
      };
    }
    return parsed as T;
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

export { isError };
export type { ApiError, ResearchDocument };

// ---- Authorisation ----

/**
 * Whether this session may see the console at all.
 *
 * Three answers, and the middle one is the whole of D-20: `absent` covers a signed-in
 * non-operator, a 500, an endpoint that is not deployed and a dead network, all
 * collapsed into one indistinguishable outcome. Nothing downstream can tell "not you"
 * from "no such page", which is the only way that rule is worth anything.
 *
 * `signed_out` is kept apart deliberately, and the API keeps it apart for the same
 * reason (see `requireOperator` in packages/api): 401 is what *any* signed-out request
 * to any endpoint gets, so answering it with a 404 discloses nothing extra and only
 * strands an operator whose session expired on a page with no way back in. The
 * disclosure argument does not apply to it either — a signed-out prober gets 401 from
 * the endpoint whatever this screen renders.
 */
export type Access = 'operator' | 'absent' | 'signed_out';

export async function operatorAccess(): Promise<Access> {
  const result = await get<{ operator: true }>('/api/admin/me');
  if (isError(result)) return result.status === 401 ? 'signed_out' : 'absent';
  return result.operator === true ? 'operator' : 'absent';
}

// ---- Learners ----

/** The per-account usage counters, as the API keeps them. */
export interface AdminUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  batchInputTokens: number;
  batchOutputTokens: number;
  calls: number;
  webSearches: number;
}

/**
 * A title as it is stored: per language, never a single string.
 *
 * The console does not translate — but the *data* is multilingual, and a screen that
 * typed this as `string` would render `[object Object]` at best and throw "objects are
 * not valid as a React child" at worst. `localTitle` in `parts.tsx` picks one.
 */
export type AdminTitle = Localised | string | undefined;

export interface AdminObjectiveRef {
  objectiveId: string;
  title: AdminTitle;
  status: ObjectiveStatus;
  /** Absent until Start was pressed. Null on an objective that never was. */
  activatedAt?: string | null;
  createdAt?: string;
  closedAt?: string | null;
}

export interface AdminWindow {
  dailyLimit: number;
  reachFrom: string;
  reachUntil: string;
  explanations: boolean;
}

export interface AdminLearner {
  userId: string;
  name: string;
  /** Masked by the API, not by this screen. The full number needs `revealPhone`. */
  phoneMasked: string;
  locale: string;
  timezone: string;
  createdAt: string;
  objective: AdminObjectiveRef | null;
  window: AdminWindow | null;
  openItemId?: string | null;
  openSince?: string | null;
  reminders: number;
  paused: boolean;
  lastInboundAt?: string | null;
  lastOutboundAt?: string | null;
  usage: AdminUsage;
}

/**
 * Which gate the sweep's own decision stopped at.
 *
 * The sweep's vocabulary, passed through rather than reinterpreted. `open` means the
 * gates are open — **not** that a question is open, which is `openItemId`; the two
 * read alike and mean opposite things, which is why the verdict on screen never says
 * the word "open" about a gate.
 */
export type SweepReason =
  | 'open'
  | 'before_window'
  | 'after_window'
  | 'limit_reached'
  | 'no_window'
  | 'no_timezone'
  | string;

/**
 * Why a learner is not being sent anything, as the sweep itself would answer it.
 *
 * Every field is a *reason for silence* the operator would otherwise infer from three
 * screens and the sweep's source: outside their hours, the day's allowance spent, a
 * question already open, sending paused, or no schedule saved at all. It exists
 * because "correctly quiet" and "broken" are indistinguishable from outside — sends
 * land whenever a five-minute sweep finds due work inside the window, so the timing
 * looks arbitrary to anybody who cannot see these values.
 */
export interface AdminSweepState {
  /* Null throughout, because a learner can exist before a schedule does — and the
     panel has to say "no window saved" rather than draw a window of nulls. */
  reachFrom: string | null;
  reachUntil: string | null;
  timezone: string | null;
  /** `YYYY-MM-DD HH:MM` in the learner's zone, computed server-side. */
  localTimeNow: string | null;
  /**
   * True only when the sweep would send *now*, which folds in the daily cap as well
   * as the clock. So this alone cannot answer "are they inside their hours" — a
   * learner who has used the day's allowance reads `false` at noon. `reason` is what
   * separates the two, and the panel uses that.
   */
  insideWindow: boolean;
  dailyLimit: number | null;
  sentToday: number;
  remainingToday: number;
  openItemId: string | null;
  openSince: string | null;
  reminders: number;
  paused: boolean;
  reason: SweepReason;
  /** The next instant a message may go out, when that is computable. */
  nextSendAt: string | null;
}

/**
 * What `/api/admin/learner` answers: three top-level keys, not a widened learner.
 *
 * Nested because the API returns it nested. It was written here as
 * `extends AdminLearner` from the contract sketch, which typechecks perfectly and
 * would have rendered an empty page against the real endpoint — the fields would all
 * have been one level too shallow. Read the handler, not the sketch.
 *
 * `sweepState` and `objectives` are optional so that a deploy without them leaves the
 * rest of the screen working; the panel says which one is missing.
 */
export interface AdminLearnerDetail {
  learner: AdminLearner;
  objectives?: AdminObjectiveRef[];
  sweepState?: AdminSweepState;
}

export const listLearners = () => get<{ learners: AdminLearner[] }>('/api/admin/learners');

export const getLearner = (userId: string) =>
  get<AdminLearnerDetail>(`/api/admin/learner?userId=${encodeURIComponent(userId)}`);

/**
 * The number in the clear, for one learner, once.
 *
 * A call rather than a field, because the server logs the read (D-21, F-81). Nothing
 * caches the answer across a reload: a revealed number that survives a refresh is a
 * number left on screen for the rest of the afternoon.
 */
export const revealPhone = (userId: string) =>
  get<{ phone: string }>(`/api/admin/phone?userId=${encodeURIComponent(userId)}`);

// ---- One objective, and what was generated for it ----

/** A curriculum node as the console reads it: the learner's tree plus the ticked flag. */
export interface AdminNode extends CurriculumNode {
  /**
   * Whether this subject is being taught. Resolved by the API rather than here.
   *
   * The learner app applies `isActiveNode` in the browser because absent means
   * active and nested topics follow their parent. The console must not repeat that
   * rule: a second opinion about which subjects are live is how a debugging tool
   * ends up disagreeing with the pipeline it is meant to explain.
   */
  active?: boolean;
}

export interface AdminObjectiveDetail {
  /** The stored row, near enough verbatim. Unlisted attributes are the API's to add. */
  objective: AdminObjectiveRef & {
    description?: string;
    locale?: string;
    userId?: string;
    createdAt?: string;
    itemVersion?: string;
    curriculumVersion?: string;
    researchError?: string;
    researchDetail?: string;
    synthesisError?: string;
    synthesisDetail?: string;
    itemsError?: string;
    itemsDetail?: string;
  };
  nodes: AdminNode[];
  /** Full body text, not a summary. This is the point of the endpoint. */
  research: ResearchDocument[];
  /**
   * The manifests that pinned each pass.
   *
   * Typed as loose records because the contract does not fix their shape and this
   * screen has no business inventing one — rendered as formatted JSON, which is
   * both honest about that and the most useful thing for a manifest anyway.
   */
  manifests: Record<string, unknown>[];
}

export const getObjective = (objectiveId: string) =>
  get<AdminObjectiveDetail>(`/api/admin/objective?objectiveId=${encodeURIComponent(objectiveId)}`);

// ---- Items ----

/** FSRS state for one item and one learner, when the item has ever been scheduled. */
export interface AdminReview {
  due: string;
  stability: number;
  difficulty: number;
  reps: number;
  lapses: number;
  /**
   * `new` | `learning` | `review` | `relearning` as a plain string, deliberately.
   *
   * Narrowing it to the union would make the console the place where an unexpected
   * stored value becomes a type error — and a console that will not compile against
   * the data it is meant to inspect is useless exactly when it is needed.
   */
  state: string;
  /**
   * When it last went out — and **null once the answer has been graded**, because
   * `sentAt` is the grader's idempotence guard and grading clears it.
   */
  sentAt?: string | null;
}

export interface AdminItem {
  itemId: string;
  nodeId: string;
  kind: 'recall' | 'explanation';
  prompt: string;
  answer?: string;
  rubric?: string;
  sourceIds: string[];
  /** Null, not absent, for an item on a curriculum nobody has started. */
  review?: AdminReview | null;
}

/**
 * One page of items, and where the next one starts.
 *
 * `nextCursor` is read with a fallback because the contract says "paginated" without
 * naming the field. An objective holds ~538 items and the console follows the cursor
 * until it runs out, so guessing wrong here would silently show the first page and
 * call it the whole objective — which is precisely the wrong failure for the screen
 * whose job is judging all of them. If neither field appears, one page *is* the
 * answer and the screen says how many it has.
 */
interface Paged {
  nextCursor?: string;
  cursor?: string;
}

export const nextCursorOf = (page: Paged): string | undefined => page.nextCursor ?? page.cursor;

export function listItems(
  objectiveId: string,
  nodeId?: string,
  cursor?: string,
  limit = 100,
): Promise<({ items: AdminItem[] } & Paged) | ApiError> {
  const query = new URLSearchParams({ objectiveId, limit: String(limit) });
  if (nodeId) query.set('nodeId', nodeId);
  if (cursor) query.set('cursor', cursor);
  return get<{ items: AdminItem[] } & Paged>(`/api/admin/items?${query.toString()}`);
}

// ---- Messages ----

/**
 * One row of the message log.
 *
 * `direction`, `at` and `text` are what `keys.message` rows have always carried.
 * Everything after them is optional because the writers disagree today: the inbound
 * claim logs no purpose, the reply logs `purpose` and `inReplyTo`, and the question
 * the learner is answering is logged by the sweep. A reader that required the union
 * of all of them would show an empty history for the rows that exist.
 */
export interface AdminMessage {
  messageId: string;
  at: string;
  direction: 'in' | 'out';
  text: string;
  purpose?: string;
  itemId?: string | null;
  outcome?: 'wrong' | 'partial' | 'correct' | 'effortless' | null;
}

export function listMessages(
  userId: string,
  cursor?: string,
  limit = 100,
): Promise<({ messages: AdminMessage[] } & Paged) | ApiError> {
  const query = new URLSearchParams({ userId, limit: String(limit) });
  if (cursor) query.set('cursor', cursor);
  return get<{ messages: AdminMessage[] } & Paged>(`/api/admin/messages?${query.toString()}`);
}

// ---- The send timeline ----

/**
 * One question that actually went out.
 *
 * Distinct from a message row, and worth its own endpoint, because the question
 * being asked is "should this have been sent when it was" — which needs the due date
 * the sweep saw at the time next to the one it wrote afterwards. A message log
 * answers "what was said"; this answers "was the schedule honoured".
 */
export interface AdminSend {
  itemId: string;
  nodeId: string;
  objectiveId: string;
  kind: string;
  /**
   * Exact, from the sweep's claim — and **null once the answer has been graded**.
   *
   * This is the timeline's one real limitation and the screen says so rather than
   * drawing a blank cell: grading clears `sentAt` (it is the idempotence guard), so an
   * answered question's send time is genuinely gone from the table. `at` is what the
   * row is placed by.
   */
  sentAt: string | null;
  /** What the timeline is ordered by: the send if known, otherwise the answer. */
  at: string;
  answeredAt: string | null;
  outcome: string | null;
  prompt: string;
  promptTruncated: boolean;
  /** The due date the send was made against. Known only while unanswered. */
  dueAtSend: string | null;
  /** Where the item stands now. */
  due: string;
  reps: number;
  lapses: number;
  state: string;
  /** Reminders for this question. Knowable only for the one currently open. */
  reminders: number | null;
}

/**
 * How many messages went out on each of the learner's local days, newest first.
 *
 * The durable half of the answer. Only one review row exists per item and grading
 * overwrites its send time, so the timeline above can lose individual sends — these
 * counters cannot, because nothing removes them. "Did anything go out on the eighth"
 * is answered here even when the row that did it has been overwritten twice since.
 */
export interface AdminSendDay {
  day: string;
  sent: number;
}

export function listSends(
  userId: string,
  cursor?: string,
  limit = 100,
): Promise<({ sends: AdminSend[]; days?: AdminSendDay[] } & Paged) | ApiError> {
  const query = new URLSearchParams({ userId, limit: String(limit) });
  if (cursor) query.set('cursor', cursor);
  return get<{ sends: AdminSend[]; days?: AdminSendDay[] } & Paged>(
    `/api/admin/sends?${query.toString()}`,
  );
}

// ---- The named writes (D-19) ----

export type RerunPass = 'research' | 'synthesis' | 'items';

/**
 * What a re-run costs, in dollars, taken from `docs/costs.md`.
 *
 * **Kept here rather than fetched, and that is the compromise being made.** The
 * figures are measured, they live in `docs/costs.md`, and they will drift from it —
 * so each one names its measurement, and this comment is the instruction to update
 * both together. The alternative was a button that spends $2.38 without saying so,
 * which is how the budget goes on an accidental click (F-83, and the $50/month
 * ceiling in docs/costs.md).
 *
 * Research is the one that matters. It is measured at ~$2.38 for a 12-search pass
 * on Opus, which is roughly a fifth of a month's entire infrastructure bill for one
 * button.
 */
export const PASS_COSTS: Record<
  RerunPass,
  { dollars: number; basis: string; what: string; measured: boolean }
> = {
  research: {
    dollars: 2.38,
    // docs/costs.md, "The research pass, measured": 70 in / 1.50M cache read /
    // 75k cache write / 41.6k out, 12 searches, on claude-opus-5.
    basis: 'measured: 12-search Opus pass, 1.50M cache read tokens',
    what: 'searches the web again and writes a new research document',
    measured: true,
  },
  synthesis: {
    dollars: 0.36,
    // docs/costs.md, "Synthesis on a larger document, measured": 23,651 in /
    // 9,723 out, producing 69 nodes.
    basis: 'measured: 47,896-character document, 69 nodes out',
    what: 'rebuilds the curriculum from the research already paid for',
    measured: true,
  },
  items: {
    dollars: 0.4,
    // docs/costs.md, "Generate items, per node": ~$0.0055/node measured, so 69
    // nodes is ~$0.38. Rounded up rather than down — a cost estimate that
    // flatters the button is worse than no estimate.
    basis: 'measured: ~$0.0055/node via Batches, at 69 nodes',
    what: 'writes every question again and replaces the current item version',
    measured: true,
  },
};

/**
 * Asks for a pass to run again. 202, or 409 with a reason.
 *
 * The confirmation and the price live on the button, not here — but the refusal is
 * the backstop that matters: an objective mid-pass answers 409 rather than starting
 * a second, so a double click costs nothing.
 */
export const rerun = (objectiveId: string, pass: RerunPass) =>
  post<{ ok?: true; accepted?: true }>('/api/admin/rerun', { objectiveId, pass });

/**
 * Runs the due sweep for one learner now, instead of waiting up to five minutes.
 *
 * Not free of consequence even though it is free of model cost: if the learner has
 * due work and is inside their window, this puts a question on their phone. The
 * confirmation says so.
 */
export const sweep = (userId: string) => post<{ ok?: true; accepted?: true }>('/api/admin/sweep', { userId });
